import { Action } from '@reduxjs/toolkit';
import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';

import { Loader } from '@eika/loader';

import { BesvartSporsmalRad, OppsummeringTabell } from '../../components/oppsummering/oppsummering';
import { AppState } from '../../reducers';
import { SavingState } from '../../reducers/savingState';
import { BesvartSporsmal } from '../../sporsmalsflyt/reducer/current-steg';
import { Skademeldingstype, SporsmalsflytSkademeldingDTO } from '../../types/generated-types';
import { Bonus, DisplayStatus, Egenandel, Forsikring, MaksKjorelengde } from '../../types/internal-types';
import { sporsmalsflytskademeldingDTOFrom } from '../reducer/skademelding-actions';
import {
    updateBonus,
    updateEgenandel,
    updateKjorelengde,
    updateSkademeldingForsikringOppsummeringSavingStateToFinished,
    updateSkademeldingForsikringOppsummeringSavingStateToOngoing,
} from '../reducer/skademeldingOppsummering-actions';
import { getBonus, getEgenandel, getMaksKjorelengde } from './motorvogn-functions';

export const Motorvogninformasjon = ({
    selectedForsikring,
    bankregnr,
    skadetype,
    flattenedBesvarteSporsmal,
}: {
    selectedForsikring: Forsikring;
    bankregnr: string;
    skadetype: Skademeldingstype;
    flattenedBesvarteSporsmal: BesvartSporsmal[];
}) => {
    const dispatch: Dispatch<Action> = useDispatch();
    const { bonus, egenandel } = useSelector(
        (state: AppState) => state.skademeldingOppsummering.forsikringOppsummeringInformation,
    );

    const { savingState } = useSelector((state: AppState) => state.skademeldingOppsummering);
    if (!selectedForsikring.versjon || !selectedForsikring.forsikringsnummer) {
        dispatch(updateSkademeldingForsikringOppsummeringSavingStateToFinished());
    }

    const skademelding: SporsmalsflytSkademeldingDTO = sporsmalsflytskademeldingDTOFrom(
        skadetype,
        flattenedBesvarteSporsmal,
    );

    const showBonus = bonus && bonus.displayStatus === DisplayStatus.Show;
    const showEgenandel = egenandel && egenandel.displayStatus === DisplayStatus.Show;
    const showTilInformasjon = savingState === SavingState.Success && (showEgenandel || showBonus);

    const getForsikringOppsummeringInformation = useCallback(() => {
        getBonus(selectedForsikring.forsikringsnummer, selectedForsikring.versjon, skadetype, bankregnr, skademelding)
            .then((data: Bonus) => dispatch(updateBonus(data)))
            .finally(() =>
                getEgenandel(selectedForsikring.forsikringsnummer, bankregnr, skademelding).then(
                    (egenandel: Egenandel) => dispatch(updateEgenandel(egenandel)),
                ),
            )
            .finally(() =>
                getMaksKjorelengde(
                    selectedForsikring.forsikringsnummer,
                    selectedForsikring.versjon,
                    bankregnr,
                    skademelding,
                ).then((data: MaksKjorelengde) => dispatch(updateKjorelengde(data))),
            )
            .finally(() => dispatch(updateSkademeldingForsikringOppsummeringSavingStateToFinished()));
    }, [
        bankregnr,
        dispatch,
        selectedForsikring.forsikringsnummer,
        selectedForsikring.versjon,
        skademelding,
        skadetype,
    ]);

    useEffect(() => {
        if (savingState === SavingState.Saving || savingState === SavingState.Success) return;

        dispatch(updateSkademeldingForsikringOppsummeringSavingStateToOngoing());
        getForsikringOppsummeringInformation();
    }, [dispatch, getForsikringOppsummeringInformation, savingState]);

    return (
        <>
            {!(savingState === SavingState.Success) && (
                <OppsummeringTabell header="Til informasjon">
                    <Loader />
                </OppsummeringTabell>
            )}

            {showTilInformasjon && (
                <OppsummeringTabell header="Til informasjon">
                    {showEgenandel && <BesvartSporsmalRad sporsmal={egenandel.tittel} svar={egenandel.innhold} />}
                    {showBonus && <BesvartSporsmalRad sporsmal={bonus.tittel} svar={bonus.innhold} />}
                </OppsummeringTabell>
            )}
        </>
    );
};
